/**
 * Шаг 2 «Проверьте, что получилось» — the proposal, as chips the user can pull
 * apart before anything is saved.
 *
 * The review screen is the feature. A model that proposes five ИКПУ codes is
 * useful only if the wrong one can be removed in a tap, and only if the person
 * can see what the search will actually deliver BEFORE committing — hence the
 * live lot count and the three real examples. Measured on prod (2026-09-23):
 * 53.7% of saved searches are a bare category, and 86.3% of those point at a
 * category with over 100 open lots. Those subscriptions drown. This screen
 * exists to make "too broad" visible at the moment it can still be fixed.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocale } from '../i18n/locale-context';
import {
  formatBudget,
  formatBudgetCompact,
  formatCount,
  formatDeadline,
  pluralKeyIndex,
  withCurrency,
} from '../lib/format';
import { previewSavedSearchCount } from '../lib/saved-search-client';
import { countEdits, isSaveable, type AiDraft } from '../lib/ai-proposal-draft';
import type { AiProposeResponse } from '../lib/ai-search-client';
import { AiSparkle, aiPrimaryClass, aiSecondaryClass } from './ai-search-cards';

const PLURAL_SUFFIX = ['one', 'few', 'many'] as const;

/** A selected value: tap the ✕ to drop it from what will be saved. */
function Chip({
  label,
  prefix,
  onRemove,
  removeLabel,
}: {
  label: string;
  prefix?: string;
  onRemove: () => void;
  removeLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onRemove}
      aria-label={removeLabel}
      className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-brand/40 bg-brand-dim px-3 py-1.5 text-[13px] font-medium text-brand transition-colors hover:border-brand"
    >
      {prefix && <span className="font-mono text-[12px] text-brand/80">{prefix}</span>}
      <span className="truncate">{label}</span>
      <span aria-hidden="true" className="text-brand/70">
        ✕
      </span>
    </button>
  );
}

/** An offered value that is NOT applied yet — a suggested exclusion, a
 *  narrowing lever. Dashed and grey precisely so it does not read as "on". */
function AddChip({
  label,
  onAdd,
  addLabel,
}: {
  label: string;
  onAdd: () => void;
  addLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onAdd}
      aria-label={addLabel}
      className="inline-flex max-w-full items-center gap-1 rounded-full border border-dashed border-edge bg-bone-surface px-3 py-1.5 text-[13px] font-medium text-ink-muted transition-colors hover:border-brand hover:text-brand"
    >
      <span aria-hidden="true">+</span>
      <span className="truncate">{label}</span>
    </button>
  );
}

/** One labelled line of chips. The count in the label is what makes a removal
 *  legible — «Коды ИКПУ · 4» going to · 3 is the feedback for the tap. */
function Row({
  label,
  count,
  children,
}: {
  label: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-edge-light py-2.5 first:border-t-0">
      <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-muted">
        {count === undefined ? label : `${label} · ${count}`}
      </span>
      {children}
    </div>
  );
}

export interface AiProposalReviewProps {
  response: AiProposeResponse;
  draft: AiDraft;
  onDraftChange: (next: AiDraft) => void;
  /** Save this draft as a saved search. */
  onSave: () => void;
  /** Open the standard saved-search builder on the result. */
  onEdit: () => void;
  /**
   * «Изменить» means «back to my description» instead (Spain,
   * `features.aiOnboardingReentry`): it saves nothing, so an unsaveable draft
   * does not disable it. Absent = `onEdit`, exactly as before.
   */
  editIsBack?: boolean;
  saving?: boolean;
  saveError?: string | null;
  /** Каталожный слой the preview count must be computed in (defaults to gov,
   *  matching the column default the create endpoint applies). */
  scope?: string;
}

export function AiProposalReview({
  response,
  draft,
  onDraftChange,
  onSave,
  onEdit,
  editIsBack = false,
  saving = false,
  saveError = null,
  scope,
}: AiProposalReviewProps) {
  const { t, locale } = useLocale();
  const { proposal, counts, examples, warnings } = response;
  // Dismissible advisories, not modal states: the user may disagree with both
  // («оставить как есть»), and the proposal underneath stays saveable either way.
  const [broadDismissed, setBroadDismissed] = useState(false);
  const [noneDismissed, setNoneDismissed] = useState(false);
  const [addingKeyword, setAddingKeyword] = useState(false);
  const [keywordDraft, setKeywordDraft] = useState('');
  // A new proposal is a new screen — banners that were dismissed for the
  // previous one must not stay dismissed for this one.
  useEffect(() => {
    setBroadDismissed(false);
    setNoneDismissed(false);
    setAddingKeyword(false);
    setKeywordDraft('');
  }, [response.draftId]);

  const edits = countEdits(proposal, draft);
  const untouched = edits === 0;

  /**
   * The live «открытых лотов» figure, recomputed from the CURRENT chips.
   *
   * Same endpoint the manual builder uses, so the number on this screen and the
   * number on the edit sheet cannot describe different queries. Debounced,
   * because removing three chips in a row is three renders and one question.
   */
  const previewInput = useMemo(
    () => ({
      categorySlugs: draft.categories.map((c) => c.slug),
      ikpuCodes: draft.ikpu.map((c) => c.code),
      nicheSlugs: draft.niches.map((n) => n.slug),
      regions: draft.regions.map((r) => r.code),
      textTerms: draft.keywords,
      excludeTerms: draft.excludes,
      ...(draft.minBudgetUzs != null ? { minBudgetUzs: draft.minBudgetUzs } : {}),
      ...(scope ? { scope } : {}),
    }),
    [draft, scope],
  );
  const [debounced, setDebounced] = useState(previewInput);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(previewInput), 350);
    return () => clearTimeout(id);
  }, [previewInput]);
  const { data: livePreview } = useQuery({
    queryKey: ['ai-preview-count', debounced],
    queryFn: () => previewSavedSearchCount(debounced),
    // Only once the user has actually changed something: the server already
    // counted the untouched proposal, and re-asking it the same question on
    // mount is a request that can only agree with the answer we have.
    enabled: !untouched && isSaveable(draft),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    retry: false,
  });
  const openNow = untouched ? counts.openNow : (livePreview ?? counts.openNow);

  function patch(next: Partial<AiDraft>) {
    onDraftChange({ ...draft, ...next });
  }

  /**
   * Narrowing levers offered when the search is too broad — every proposal
   * value that is NOT currently applied and would REDUCE the result: a
   * suggested exclusion, a word or region the user removed, the budget floor.
   *
   * Only unapplied ones, because a chip offering something already on would do
   * nothing when tapped, and a control that does nothing is worse than no
   * control.
   */
  const narrowing = useMemo(() => {
    const out: Array<{ key: string; label: string; apply: () => void }> = [];
    for (const kw of proposal.keywords) {
      if (!draft.keywords.includes(kw)) {
        out.push({
          key: `kw:${kw}`,
          label: kw,
          apply: () => patch({ keywords: [...draft.keywords, kw] }),
        });
      }
    }
    for (const r of proposal.regions) {
      if (!draft.regions.some((x) => x.code === r.code)) {
        out.push({
          key: `region:${r.code}`,
          label: r.name,
          apply: () => patch({ regions: [...draft.regions, r] }),
        });
      }
    }
    for (const ex of proposal.suggestedExcludes) {
      if (!draft.excludes.includes(ex)) {
        out.push({
          key: `ex:${ex}`,
          label: ex,
          apply: () => patch({ excludes: [...draft.excludes, ex] }),
        });
      }
    }
    if (proposal.minBudgetUzs != null && draft.minBudgetUzs == null) {
      const amount = formatBudgetCompact(String(proposal.minBudgetUzs), locale) ?? '';
      out.push({
        key: 'budget',
        label: t('aiOnboarding.broad.budgetChip', { amount }),
        apply: () => patch({ minBudgetUzs: proposal.minBudgetUzs }),
      });
    }
    // Every closure above reads `draft`, which is a dependency — so the memo
    // is recomputed whenever a chip moves and no `apply` can act on a stale copy.
    return out;
  }, [proposal, draft, locale, t]);

  /** Apply every narrowing lever at once — what «Сузить» does. */
  function narrowAll() {
    const keywords = [...draft.keywords];
    const regions = [...draft.regions];
    const excludes = [...draft.excludes];
    for (const kw of proposal.keywords) if (!keywords.includes(kw)) keywords.push(kw);
    for (const r of proposal.regions) if (!regions.some((x) => x.code === r.code)) regions.push(r);
    for (const ex of proposal.suggestedExcludes) if (!excludes.includes(ex)) excludes.push(ex);
    onDraftChange({
      ...draft,
      keywords,
      regions,
      excludes,
      minBudgetUzs: draft.minBudgetUzs ?? proposal.minBudgetUzs,
    });
    setBroadDismissed(true);
  }

  /**
   * «Расширить» — drop the axes that narrow, keep the subject and the words.
   *
   * Regions, the budget floor and accepted exclusions are the levers that can
   * take a live catalogue down to zero; the ИКПУ/niche subject is what the
   * search IS and removing it would leave nothing to match on at all.
   */
  function widen() {
    onDraftChange({ ...draft, regions: [], excludes: [], minBudgetUzs: null });
    setNoneDismissed(true);
  }

  const lockedCount = proposal.locked.ikpu.length;
  // What the server widened (Kazakhstan only — `proposal.widened` is never set
  // on Uzbekistan). A dropped region or budget floor is offered back as a «+»
  // chip until the user puts it back.
  const widened = proposal.widened && proposal.widened.steps.length > 0 ? proposal.widened : null;
  const restoreRegions = (widened?.droppedRegions ?? []).filter(
    (r) => !draft.regions.some((x) => x.code === r.code),
  );
  const restoreBudget =
    widened?.droppedMinBudgetUzs != null && draft.minBudgetUzs == null
      ? widened.droppedMinBudgetUzs
      : null;
  const restoreBudgetLabel =
    restoreBudget === null
      ? ''
      : t('aiOnboarding.broad.budgetChip', {
          amount: formatBudgetCompact(String(restoreBudget), locale) ?? '',
        });
  const showBroad = warnings.includes('too_broad') && !broadDismissed;
  const showNone = warnings.includes('none_now') && !noneDismissed && openNow === 0;

  function commitKeyword() {
    const term = keywordDraft.trim();
    setKeywordDraft('');
    setAddingKeyword(false);
    if (!term || draft.keywords.includes(term)) return;
    patch({ keywords: [...draft.keywords, term] });
  }

  return (
    <div>
      <p className="mb-3 flex items-start gap-2 rounded-lg border border-brand/20 bg-brand-dim px-3 py-2 text-[12.5px] leading-snug text-ink-soft">
        <AiSparkle className="mt-[1px]" />
        {t('aiOnboarding.step2.byAi')}
      </p>

      {widened && (
        <section
          aria-label={t('aiOnboarding.widened.title')}
          className="mb-3 rounded-xl border border-edge bg-bone p-3"
        >
          <h3 className="mb-1 text-[14px] font-bold text-ink">{t('aiOnboarding.widened.title')}</h3>
          <p className="mb-1 text-[12.5px] leading-snug text-ink-soft">
            {t('aiOnboarding.widened.desc', { n: formatCount(widened.openBefore) })}
          </p>
          <ul className="list-disc pl-5 text-[12.5px] leading-snug text-ink-soft">
            {widened.steps.map((step) => (
              <li key={step}>{t(`aiOnboarding.widened.${step}`)}</li>
            ))}
          </ul>
          {(restoreRegions.length > 0 || restoreBudget !== null) && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-[12px] text-ink-muted">
                {t('aiOnboarding.widened.restore')}
              </span>
              {restoreRegions.map((r) => (
                <AddChip
                  key={r.code}
                  label={r.name}
                  addLabel={t('aiOnboarding.chipAdd', { label: r.name })}
                  onAdd={() => patch({ regions: [...draft.regions, r] })}
                />
              ))}
              {restoreBudget !== null && (
                <AddChip
                  label={restoreBudgetLabel}
                  addLabel={t('aiOnboarding.chipAdd', { label: restoreBudgetLabel })}
                  onAdd={() => patch({ minBudgetUzs: restoreBudget })}
                />
              )}
            </div>
          )}
        </section>
      )}

      {showBroad && (
        /* A landmark, not a plain div: the advisory and the chip rows below it
           offer the SAME «+ аренда» control, and without a named region neither
           a screen-reader user nor a test can say which one they are on. */
        <section
          aria-label={t('aiOnboarding.broad.title', { n: formatCount(openNow) })}
          className="mb-3 rounded-xl border border-status-closing-dot/40 bg-status-closing-bg p-3"
        >
          <h3 className="mb-1 text-[14px] font-bold text-status-closing-text">
            {t('aiOnboarding.broad.title', { n: formatCount(openNow) })}
          </h3>
          <p className="mb-2 text-[12.5px] leading-snug text-ink-soft">
            {t('aiOnboarding.broad.desc')}
          </p>
          {narrowing.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {narrowing.map((n) => (
                <AddChip
                  key={n.key}
                  label={n.label}
                  onAdd={n.apply}
                  addLabel={t('aiOnboarding.chipAdd', { label: n.label })}
                />
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2 [&>*]:min-w-[120px] [&>*]:flex-1">
            <button type="button" onClick={narrowAll} className={aiPrimaryClass}>
              {t('aiOnboarding.broad.narrow')}
            </button>
            <button
              type="button"
              onClick={() => setBroadDismissed(true)}
              className={aiSecondaryClass}
            >
              {t('aiOnboarding.broad.keep')}
            </button>
          </div>
        </section>
      )}

      {showNone && (
        <section
          aria-label={t('aiOnboarding.none.title')}
          className="mb-3 rounded-xl border border-edge bg-bone p-3"
        >
          <h3 className="mb-1 text-[14px] font-bold text-ink">{t('aiOnboarding.none.title')}</h3>
          <p className="mb-2 text-[12.5px] leading-snug text-ink-soft">
            {t('aiOnboarding.none.desc')}
          </p>
          <div className="flex flex-wrap gap-2 [&>*]:min-w-[120px] [&>*]:flex-1">
            <button
              type="button"
              onClick={onSave}
              disabled={saving || !isSaveable(draft)}
              className={aiPrimaryClass}
            >
              {t('aiOnboarding.none.save')}
            </button>
            <button type="button" onClick={widen} className={aiSecondaryClass}>
              {t('aiOnboarding.none.widen')}
            </button>
          </div>
        </section>
      )}

      <div className="rounded-lg border border-edge bg-bone-surface px-3 py-1">
        {draft.categories.length > 0 && (
          <Row label={t('aiOnboarding.row.categories')} count={draft.categories.length}>
            {draft.categories.map((c) => (
              <Chip
                key={c.slug}
                label={c.name}
                removeLabel={t('aiOnboarding.chipRemove', { label: c.name })}
                onRemove={() =>
                  patch({ categories: draft.categories.filter((x) => x.slug !== c.slug) })
                }
              />
            ))}
          </Row>
        )}

        {draft.ikpu.length > 0 && (
          <Row label={t('aiOnboarding.row.ikpu')} count={draft.ikpu.length}>
            {draft.ikpu.map((c) => (
              <Chip
                key={c.code}
                prefix={c.code}
                label={c.name}
                removeLabel={t('aiOnboarding.chipRemove', { label: `${c.code} ${c.name}` })}
                onRemove={() => patch({ ikpu: draft.ikpu.filter((x) => x.code !== c.code) })}
              />
            ))}
          </Row>
        )}

        {draft.niches.length > 0 && (
          <Row label={t('aiOnboarding.row.niches')} count={draft.niches.length}>
            {draft.niches.map((n) => (
              <Chip
                key={n.slug}
                label={n.name}
                removeLabel={t('aiOnboarding.chipRemove', { label: n.name })}
                onRemove={() => patch({ niches: draft.niches.filter((x) => x.slug !== n.slug) })}
              />
            ))}
          </Row>
        )}

        {/* Words are NOT optional garnish: `uzex-xarid` publishes no product
            code at all and `procurement_kind` is empty exactly where ИКПУ is,
            so a code-only search misses those lots outright. Hence the add
            control lives here and not behind «Изменить». */}
        <Row label={t('aiOnboarding.row.keywords')} count={draft.keywords.length}>
          {draft.keywords.map((k) => (
            <Chip
              key={k}
              label={k}
              removeLabel={t('aiOnboarding.chipRemove', { label: k })}
              onRemove={() => patch({ keywords: draft.keywords.filter((x) => x !== k) })}
            />
          ))}
          {addingKeyword ? (
            <input
              autoFocus
              type="text"
              value={keywordDraft}
              aria-label={t('aiOnboarding.row.keywords')}
              onChange={(e) => setKeywordDraft(e.target.value)}
              onBlur={commitKeyword}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitKeyword();
                }
                if (e.key === 'Escape') {
                  setKeywordDraft('');
                  setAddingKeyword(false);
                }
              }}
              className="h-9 w-32 rounded-full border border-edge bg-bone px-3 text-[16px] text-ink outline-none focus:border-brand"
            />
          ) : (
            <AddChip
              label={t('aiOnboarding.addWord')}
              onAdd={() => setAddingKeyword(true)}
              addLabel={t('aiOnboarding.addWord')}
            />
          )}
        </Row>

        {draft.regions.length > 0 && (
          <Row label={t('aiOnboarding.row.regions')} count={draft.regions.length}>
            {draft.regions.map((r) => (
              <Chip
                key={r.code}
                label={r.name}
                removeLabel={t('aiOnboarding.chipRemove', { label: r.name })}
                onRemove={() => patch({ regions: draft.regions.filter((x) => x.code !== r.code) })}
              />
            ))}
          </Row>
        )}

        {/* Suggested exclusions arrive UNAPPLIED — see ai-proposal-draft.ts.
            A negative filter the user never agreed to is an alert that goes
            quiet for a reason nobody can see. */}
        {(proposal.suggestedExcludes.length > 0 || draft.excludes.length > 0) && (
          <Row label={t('aiOnboarding.row.excludes')} count={draft.excludes.length}>
            {draft.excludes.map((ex) => (
              <Chip
                key={ex}
                label={ex}
                removeLabel={t('aiOnboarding.chipRemove', { label: ex })}
                onRemove={() => patch({ excludes: draft.excludes.filter((x) => x !== ex) })}
              />
            ))}
            {proposal.suggestedExcludes
              .filter((ex) => !draft.excludes.includes(ex))
              .map((ex) => (
                <AddChip
                  key={ex}
                  label={ex}
                  onAdd={() => patch({ excludes: [...draft.excludes, ex] })}
                  addLabel={t('aiOnboarding.chipAdd', { label: ex })}
                />
              ))}
          </Row>
        )}
      </div>

      {/* Free tier: the proposal was folded into one category, and the codes
          that did not fit are NAMED as withheld rather than quietly dropped.
          "We found nothing else" and "we found more and it needs Pro" are very
          different statements and only one of them is true. */}
      {lockedCount > 0 && (
        <p className="mt-2 text-[12.5px] text-ink-muted">
          {t('aiOnboarding.locked', {
            n: lockedCount,
            word: t(`aiOnboarding.code.${PLURAL_SUFFIX[pluralKeyIndex(lockedCount)]}`),
          })}
        </p>
      )}

      {/* The live line. `perDay` is a 14-day server-side average and cannot be
          recomputed in the browser, so it is shown only while the draft still
          matches what the server measured — a stale rate beside a fresh count
          would be two numbers describing two different searches. */}
      <p className="mt-3 flex items-center gap-2 rounded-lg bg-status-active-bg px-3 py-2 text-[13px] font-semibold text-status-active-text">
        <span aria-hidden="true" className="h-2 w-2 rounded-full bg-status-active-dot" />
        {untouched
          ? t('aiOnboarding.counts', {
              open: formatCount(openNow),
              perDay: formatCount(counts.perDay),
            })
          : t('saved.matches', { n: formatCount(openNow) })}
      </p>

      {examples.length > 0 && (
        <ul className="mt-2 flex flex-col gap-2">
          {examples.slice(0, 3).map((ex) => (
            <li key={ex.id} className="rounded-lg border border-edge bg-bone-surface p-3">
              <p className="text-[13px] font-semibold leading-snug text-ink">{ex.title}</p>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-ink-muted">
                {ex.buyerName && <span className="min-w-0 truncate">{ex.buyerName}</span>}
                {ex.deadline && (
                  <span className="font-semibold text-orange-ink">
                    {formatDeadline(ex.deadline, locale)}
                  </span>
                )}
                {ex.budgetUzs != null && (
                  <span className="font-semibold text-ink-soft">
                    {withCurrency(formatBudget(String(ex.budgetUzs)), t('tender.budget.unit'))}
                  </span>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}

      {saveError && (
        <p className="mt-2 text-[12.5px] text-danger" role="alert">
          {saveError}
        </p>
      )}

      {/* Sticky, because the chip rows can run past a phone screen and the CTA
          must not be something you scroll to find. */}
      <div className="sticky bottom-0 -mx-5 mt-3 flex gap-2 border-t border-edge bg-bone-surface px-5 pb-1 pt-3 md:-mx-6 md:px-6">
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !isSaveable(draft)}
          className={`${aiPrimaryClass} h-12 flex-[2] whitespace-normal leading-tight`}
        >
          {saving ? t('aiOnboarding.saving') : t('aiOnboarding.save')}
        </button>
        <button
          type="button"
          onClick={onEdit}
          disabled={saving || (!editIsBack && !isSaveable(draft))}
          className={`${aiSecondaryClass} h-12 flex-1`}
        >
          {t('aiOnboarding.edit')}
        </button>
      </div>
    </div>
  );
}
